import { keepPreviousData, useMutation, useQuery, UseQueryResult } from '@tanstack/react-query'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { callToCheckLoginUser, callToGetGridColumns, callToGetMenuGrids, callToGetMenuList, callToSaveGridColumns, callToSignUp } from '@/services/auth/apis'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { MenuGrid, MenuParentModel } from '@/services/auth/menu.types'
import { GridColumnState } from '@/services/auth/gridColumn.types'

const PATH = ['auth']
const GET_MENU_LIST = [...PATH, 'GET_MENU_LIST']

export const gridColumnsQueryKeys = {
  all: [...PATH, 'GRID_COLUMNS'] as const,
  detail: (gridKey: string, shipperCode: string) => [...gridColumnsQueryKeys.all, gridKey, shipperCode] as const,
}

export const useCheckUser = () => {
  return useMutation({
    mutationFn: () => callToCheckLoginUser(),
  })
}

export const useSignUp = () => {
  return useMutation({
    mutationFn: callToSignUp,
  })
}

export const useGetMenuList = (): UseQueryResult<MenuParentModel[], Error> => {
  return useQuery({
    queryKey: GET_MENU_LIST,
    enabled: false,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const userInfo = AuthStorage.user.get()
      const { AUTH_CD = '', company = '', email = '' } = userInfo

      const menuList = await callToGetMenuList({ AUTH_CD, SPR_NM: company, email })
      MenuStorage.menu.set(menuList)

      return menuList
    },
    select: (data) =>
      data.map((parent) => ({
        menuCode: parent.MENU_CD.toLowerCase(),
        menuName: parent.MENU_NM,
        menuItem:
          parent.MENU_ITEM?.map((child) => ({
            menuCode: child.MENU_CD.toLowerCase(),
            menuName: child.MENU_NM,
            link: `/${parent.MENU_CD.toLowerCase()}/${child.MENU_CD.toLowerCase()}`,
            allowedButtons: child.allowedButtons,
            toolbar: child.toolbar,
            editable: child.editable,
          })) ?? [],
      })),
    meta: {
      noErrorMessage: true,
    },
  })
}

export const menuGridsQueryKeys = {
  all: [...PATH, 'MENU_GRIDS'] as const,
  detail: (menuCode: string, shipperCode: string) => [...menuGridsQueryKeys.all, menuCode, shipperCode] as const,
}

// LOGISPRD-17094 — 화면관리 그리드 설정. 화주는 메뉴 목록과 같은 로그인 화주. 실패는 화면을 막지 않는다(data 없음 → 지금 동작).
export const useGetMenuGrids = (menuCode: string, enabled = true): UseQueryResult<MenuGrid[] | null, Error> => {
  const { company = '' } = AuthStorage.user.get()
  // 캐시 키와 요청이 같은 값을 쓴다 — 'gi38'·'GI38' 로 불러도 한 캐시다.
  const code = menuCode.toUpperCase()
  return useQuery({
    queryKey: menuGridsQueryKeys.detail(code, company),
    enabled: enabled && !!company,
    staleTime: Infinity,
    queryFn: () => callToGetMenuGrids(code, company),
    meta: {
      noErrorMessage: true,
    },
  })
}

// LOGISPRD-15456 — 저장값이 없고 legacyColumns(GI23~26 예전 localStorage 배치)가 있으면 그걸 이 화주의 저장값으로 올리고 쓴다.
// 조회·저장 실패는 화면을 막지 않는다 — 기본 배치로 그린다.
export const useGetGridColumns = (gridKey: string | undefined, shipperCode: string, legacyColumns: GridColumnState[]): UseQueryResult<GridColumnState[], Error> => {
  return useQuery({
    // legacyColumns 는 queryKey 에서 뺀다 — 이 코드가 쓰지도 지우지도 않는 localStorage 값이라 캐시가 지워져 다시 조회해도 같은 값이고,
    // useTableColumnDefs 의 setQueryData 가 같은 키(gridKey·화주)로 저장값을 갱신해야 한다.
    queryKey: gridColumnsQueryKeys.detail(gridKey ?? '', shipperCode),
    enabled: !!gridKey && !!shipperCode,
    staleTime: Infinity,
    queryFn: async () => {
      const items = await callToGetGridColumns(gridKey ?? '', shipperCode)
      if (items.length > 0 || legacyColumns.length === 0) return items

      callToSaveGridColumns(gridKey ?? '', shipperCode, legacyColumns).catch(() => undefined)
      return legacyColumns
    },
    meta: {
      noErrorMessage: true,
    },
  })
}
